import React, { useEffect, useState } from 'react';
import { getCardPriceData, CardMarketData, EBayData } from '../lib/marketConnectors.ts';

interface MarketWidgetProps {
  cardId: string;
  cardName: string;
  setName?: string;
}

export default function MarketWidget({ cardId, cardName, setName }: MarketWidgetProps) {
  const [loading, setLoading] = useState(false);
  const [cardMarketData, setCardMarketData] = useState<CardMarketData | null>(null);
  const [eBayData, setEBayData] = useState<EBayData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getCardPriceData(cardId, cardName, setName);
        
        if (!cancelled) {
          setCardMarketData(data.cardmarket || null);
          setEBayData(data.ebay || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load market data');
          console.error('Market data fetch error:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      cancelled = true;
    };
  }, [cardId, cardName, setName]);

  const formatPrice = (price: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3 text-white">Market Prices</h3>
        <div className="space-y-2">
          <div className="animate-pulse bg-gray-700 h-4 rounded"></div>
          <div className="animate-pulse bg-gray-700 h-4 rounded w-3/4"></div>
          <div className="animate-pulse bg-gray-700 h-4 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3 text-white">Market Prices</h3>
        <div className="text-red-400 text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-4 text-white">Market Prices</h3>
      
      <div className="space-y-4">
        {/* CardMarket Section */}
        {cardMarketData && (
          <div className="border-b border-gray-600 pb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-white">CardMarket</h4>
              <span className="text-xs text-gray-400">
                Updated {formatDate(cardMarketData.updatedAt)}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              {cardMarketData.prices.averageSellPrice && (
                <div>
                  <span className="text-gray-400">Avg Sell:</span>
                  <span className="text-white ml-1">
                    {formatPrice(cardMarketData.prices.averageSellPrice)}
                  </span>
                </div>
              )}
              {cardMarketData.prices.lowPrice && (
                <div>
                  <span className="text-gray-400">Low:</span>
                  <span className="text-white ml-1">
                    {formatPrice(cardMarketData.prices.lowPrice)}
                  </span>
                </div>
              )}
              {cardMarketData.prices.trendPrice && (
                <div>
                  <span className="text-gray-400">Trend:</span>
                  <span className="text-white ml-1">
                    {formatPrice(cardMarketData.prices.trendPrice)}
                  </span>
                </div>
              )}
            </div>
            
            {cardMarketData.url && (
              <a
                href={cardMarketData.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-blue-400 hover:text-blue-300 text-sm"
              >
                View on CardMarket →
              </a>
            )}
          </div>
        )}

        {/* eBay Section */}
        {eBayData && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-white">eBay</h4>
              <span className="text-xs text-gray-400">
                Updated {formatDate(eBayData.updatedAt)}
              </span>
            </div>
            
            {eBayData.averagePrice && (
              <div className="text-sm mb-2">
                <span className="text-gray-400">Avg Price:</span>
                <span className="text-white ml-1">
                  {formatPrice(eBayData.averagePrice, 'USD')}
                </span>
              </div>
            )}
            
            {eBayData.recentSales.length > 0 && (
              <div className="mb-3">
                <h5 className="text-sm font-medium text-gray-300 mb-1">Recent Sales</h5>
                <div className="space-y-1">
                  {eBayData.recentSales.slice(0, 3).map((sale) => (
                    <div key={sale.id} className="text-xs text-gray-400 flex justify-between">
                      <span className="truncate">{sale.condition}</span>
                      <span className="text-white">{formatPrice(sale.price, sale.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {eBayData.activeListings.length > 0 && (
              <a
                href={eBayData.activeListings[0]?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-blue-400 hover:text-blue-300 text-sm"
              >
                View on eBay →
              </a>
            )}
          </div>
        )}

        {!cardMarketData && !eBayData && (
          <div className="text-gray-400 text-sm">No market data available</div>
        )}
      </div>
    </div>
  );
}
